import PropTypes from "prop-types";
import styled, { keyframes } from "styled-components";
import tokens from "../../theme/tokens";

const shimmer = keyframes`
  0%   { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
`;

const SkeletonWrapper = styled.div`
  padding: 8px 0;
`;

const SkeletonHeader = styled.div`
  display: grid;
  grid-template-columns: ${(p) => p.$columns};
  gap: 16px;
  padding: 10px 16px;
  border-bottom: 1px solid ${tokens.border.default};
  background: ${tokens.surface.page};
  font-family: ${tokens.typography.fontFamily};
  font-size: 11px;
  font-weight: ${tokens.typography.weight.semibold};
  letter-spacing: 0.04em;
  color: ${tokens.text.secondary};
  text-transform: uppercase;
`;

const SkeletonRow = styled.div`
  display: grid;
  grid-template-columns: ${(p) => p.$columns};
  gap: 16px;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid ${tokens.border.light};
  min-height: 56px;
`;

const Bar = styled.div`
  height: ${(p) => p.$height || 12}px;
  width: ${(p) => p.$width || "80%"};
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    ${tokens.border.light} 0%,
    ${tokens.surface.hover} 50%,
    ${tokens.border.light} 100%
  );
  background-size: 200px 100%;
  background-repeat: no-repeat;
  animation: ${shimmer} 1.4s ease-in-out infinite;
`;

const Pill = styled(Bar)`
  border-radius: 9999px;
  height: 20px;
`;

const Stacked = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

/**
 * Six-column loading placeholder for the matter "home" list, matching the
 * redesigned `MatterTableColumn`:
 *   Matter | Activity | Practice / Type | Team | Created | Status
 *
 * Layout-stable: the grid + row height match the live table so the
 * transition from loading → loaded doesn't jump. Same opt-in pattern as
 * `MatterDetailsSkeleton` — passed through `Table`'s `loadingComponent`
 * prop; not a generic primitive.
 */
const MatterListSkeleton = ({ rows = 8 }) => {
  const columns =
    "minmax(0, 2.4fr) minmax(0, 1.3fr) minmax(0, 1fr) minmax(0, 0.9fr) 120px 96px";

  return (
    <SkeletonWrapper aria-busy="true" aria-live="polite">
      <SkeletonHeader $columns={columns}>
        <span>Matter</span>
        <span>Activity</span>
        <span>Practice / Type</span>
        <span>Team</span>
        <span>Created</span>
        <span>Status</span>
      </SkeletonHeader>
      {Array.from({ length: rows }).map((_, idx) => (
        <SkeletonRow key={idx} $columns={columns}>
          <Bar $width={`${50 + ((idx * 11) % 40)}%`} $height={12} />
          <Stacked>
            <Pill $width={idx % 3 === 0 ? "140px" : "96px"} />
            <Bar $width="80px" $height={10} />
          </Stacked>
          <Stacked>
            <Bar $width="70%" $height={12} />
            <Bar $width="45%" $height={10} />
          </Stacked>
          <Stacked>
            <Bar $width="60%" $height={11} />
            <Bar $width="50%" $height={10} />
          </Stacked>
          <Bar $width="90px" $height={12} />
          <Pill $width="64px" />
        </SkeletonRow>
      ))}
    </SkeletonWrapper>
  );
};

MatterListSkeleton.propTypes = {
  rows: PropTypes.number,
};

export default MatterListSkeleton;
